import { useState, useRef } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOptions, ComboboxOption } from "@headlessui/react";

/**
 * Styled searchable company combobox.
 * Props:
 *   companies      – array of { id, name, domain }
 *   value          – currently selected company object (or null)
 *   onChange       – called with the selected company object (or null)
 *   disabled       – boolean
 *   inputClassName – override input styling (for compact table variant)
 *   onAddNew       – if provided, renders a "+ הוסף חברה חדשה" footer button
 */
export default function CompanySelect({
  companies = [],
  value,
  onChange,
  disabled,
  inputClassName,
  onAddNew,
  floating = false,
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const filtered = query
    ? companies.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    : companies;

  const defaultInputCls =
    "w-40 border border-gray-200 rounded-md px-2 py-1 text-sm text-gray-700 " +
    "focus:outline-none focus:ring-1 focus:ring-brand-400 " +
    "disabled:opacity-50 disabled:cursor-wait";

  return (
    <Combobox by="id" value={value} onChange={onChange} disabled={disabled}>
      <div className="relative">
        <ComboboxInput
          ref={inputRef}
          className={inputClassName ?? defaultInputCls}
          displayValue={(c) => c?.name ?? ""}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => setQuery("")}
          placeholder="בחר חברה..."
        />
        <ComboboxButton
          type="button"
          className="absolute left-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600 disabled:cursor-wait"
          aria-label="פתח רשימת חברות"
        >
          ▾
        </ComboboxButton>
        <ComboboxOptions
          anchor={floating ? "bottom start" : undefined}
          portal={floating}
          className="z-50 mt-1 w-56 rounded-lg bg-white shadow-lg border border-gray-200 text-sm max-h-[280px] overflow-y-auto [--anchor-gap:4px]"
          dir="rtl"
        >
          <ComboboxOption
            value={null}
            className="px-3 py-2 cursor-pointer data-[focus]:bg-gray-50 text-gray-400 italic"
          >
            ללא חברה
          </ComboboxOption>
          {filtered.map((c) => (
            <ComboboxOption
              key={c.id}
              value={c}
              className="px-3 py-2 cursor-pointer data-[focus]:bg-blue-50 text-gray-700"
            >
              {c.name}
            </ComboboxOption>
          ))}
          {onAddNew && (
            <div className="border-t sticky bottom-0 bg-white">
              <button
                type="button"
                className="w-full text-right px-3 py-2 text-sm text-brand-600 hover:bg-blue-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onAddNew();
                  inputRef.current?.blur();
                }}
              >
                + הוסף חברה חדשה
              </button>
            </div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
